import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Newspaper, MessageSquare, Car, User, LogOut, Menu, Flag, X, Shield, Users, LayoutDashboard, Trophy, Zap, Settings, Mic, MapPin, Handshake } from 'lucide-react';

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navLinks = [
    { path: '/', label: 'Home', icon: Flag },
    { path: '/news', label: 'News', icon: Newspaper },
    { path: '/forum', label: 'Forum', icon: MessageSquare },
    { path: '/marketplace', label: 'Cars For Sale', icon: Car },
    { path: '/tracks', label: 'Track Atlas', icon: MapPin },
    { path: '/driver-of-the-week', label: 'Driver of the Week', icon: Trophy },
    { path: '/sponsors', label: 'Sponsors', icon: Handshake },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-white/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded bg-[#FF3B30] flex items-center justify-center">
              <Flag className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-xl tracking-tight text-white">CROWN VIC NATION</h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">Short Track Racing</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-white bg-[#FF3B30]'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 text-white" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626]">
                  <DropdownMenuItem asChild>
                    <Link to={`/profile/${user.id}`} className="cursor-pointer" data-testid="my-profile-link">
                      <User className="w-4 h-4 mr-2" />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile/edit" className="cursor-pointer" data-testid="edit-profile-link">
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/dashboard" className="cursor-pointer" data-testid="admin-dashboard-link">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/news" className="cursor-pointer" data-testid="admin-news-link">
                        <Shield className="w-4 h-4 mr-2" />
                        Manage News
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/users" className="cursor-pointer" data-testid="admin-users-link">
                        <Users className="w-4 h-4 mr-2" />
                        Manage Users
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/interviews" className="cursor-pointer" data-testid="admin-interviews-link">
                        <Mic className="w-4 h-4 mr-2" />
                        Manage Interviews
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/sponsors" className="cursor-pointer" data-testid="admin-sponsors-link">
                        <Handshake className="w-4 h-4 mr-2" />
                        Manage Sponsors
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/racing-intel" className="cursor-pointer" data-testid="admin-racing-intel-link">
                        <Zap className="w-4 h-4 mr-2" />
                        Racing Intel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/my-listings" className="cursor-pointer" data-testid="my-listings-link">
                      <Car className="w-4 h-4 mr-2" />
                      My Listings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-white" data-testid="login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white" data-testid="register-btn">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-white/10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-white bg-[#FF3B30]/20'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
